<section>
  @if (
    !ticket.IsClosed &&
    ticket.Business_Purpose__c !== BusinessPurpose.NOCProactiveCustomerAlert
  ) {
    <div class="w-full" *ngxPermissionsOnly="Permission.TICKETING_WRITE">
      <form
        class="pt-1 pb-2 transition-all duration-300 ease-in-out [&.ng-invalid_button:hover]:opacity-50"
        (ngSubmit)="!isCommentSubmitting() ? saveFormComment(commentForm) : ''"
        [formGroup]="commentForm"
        aria-label="Post a comment"
      >
        <div
          class="flex gap-2 lg:gap-3"
          (mouseleave)="onCommentChanges(commentText.value)"
        >
          <!-- Current user avatar -->
          <div
            class="mt-0.5 hidden h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 lg:flex"
            aria-hidden="true"
          >
            <span class="font-lato text-xs font-bold text-blue-900">{{
              currentUserInitials() || '?'
            }}</span>
          </div>

          <!-- Composer card -->
          <div class="min-w-0 flex-1">
            <textarea
              id="comment-body"
              formControlName="Body__c"
              class="comcast-textarea !mt-0 min-h-[88px] w-full text-sm lg:min-h-[96px]"
              placeholder="Post a comment..."
              rows="3"
              spellcheck="true"
              required
              aria-required="true"
              #commentText
              (input)="onCommentChanges(commentText.value)"
            ></textarea>
            <!-- Footer: inline feedback + send button -->
            <div
              class="mt-2 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between lg:gap-3"
            >
              <div class="min-w-0">
                @if (!isCommentEmpty() && !isCommentSubmitting()) {
                  @if (hasError()) {
                    <p
                      class="font-lato flex items-center gap-1.5 text-xs text-red-600"
                      role="alert"
                    >
                      <mat-icon
                        class="!flex !h-4 !w-4 !items-center !justify-center !text-sm"
                        aria-hidden="true"
                        >error_outline</mat-icon
                      >
                      Could not submit. Please try again.
                    </p>
                  } @else {
                    <p
                      class="font-lato text-brand-muted flex items-center gap-1.5 text-xs"
                    >
                      <mat-icon
                        class="!flex !h-4 !w-4 !items-center !justify-center !text-sm"
                        aria-hidden="true"
                        >info_outline</mat-icon
                      >
                      Unsaved — click Send to submit.
                    </p>
                  }
                }
              </div>
              <button
                type="submit"
                class="font-lato inline-flex h-10 w-full shrink-0 items-center justify-center gap-1.5 rounded-md bg-blue-700 px-4 py-1.5 text-sm font-bold tracking-wide text-white uppercase transition-colors hover:bg-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 disabled:cursor-not-allowed disabled:opacity-50 lg:h-auto lg:w-auto"
                [disabled]="!commentForm.valid || isCommentSubmitting()"
                [attr.aria-busy]="isCommentSubmitting()"
                aria-label="Send comment"
              >
                @if (isCommentSubmitting()) {
                  <span
                    class="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white"
                    aria-hidden="true"
                  ></span>
                  Sending…
                } @else {
                  Send
                }
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  }

  @if (ticket?.Comments__r?.records?.length) {
    <section
      aria-label="Comments"
      class="relative mt-4 pb-4"
      #commentsContainer
    >
      <div class="space-y-4 lg:space-y-5">
        @for (
          comment of ticket.Comments__r.records;
          track comment.Id;
          let i = $index
        ) {
          <div>
            @if (!comment?.Is_Read__c) {
              <div class="mb-2 flex items-center gap-2.5">
                <span
                  class="font-lato shrink-0 rounded-full bg-blue-700 px-2.5 py-0.5 text-[10px] font-bold tracking-wide text-white uppercase"
                  >New</span
                >
                <div class="h-px flex-1 bg-blue-100"></div>
              </div>
            }
            <div class="flex items-start gap-2 pb-3 lg:gap-3 lg:pb-4">
              <div
                class="font-lato flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-black lg:h-8 lg:w-8 lg:text-xs"
                [class.bg-blue-100]="isMyComment(comment)"
                [class.bg-indigo-50]="isSystemComment(comment)"
                [class.bg-gray-100]="
                  !isMyComment(comment) && !isSystemComment(comment)
                "
                aria-hidden="true"
              >
                {{ getCommentInitials(comment) }}
              </div>
              <div class="min-w-0 flex-1">
                <div
                  class="flex flex-col gap-0.5 pb-1 lg:flex-row lg:items-baseline lg:justify-between lg:gap-2"
                >
                  <!-- If comment creator is created by the user/contact, show user's fullname
            If comment is not created by contact or user, show Nite's employee's first name only -->
                  <div
                    class="font-lato min-w-0 truncate text-sm font-semibold text-gray-700"
                  >
                    @if (
                      comment?.Contact__r?.Name && !comment?.User__r?.FirstName
                    ) {
                      {{
                        comment.Contact__r.Name === 'System User'
                          ? 'Comcast Business'
                          : (comment.Contact__r.Name | titlecase)
                      }}
                    }
                    @if (
                      !comment?.User__r?.FirstName && !comment?.Contact__r?.Name
                    ) {
                      {{
                        comment?.CreatedBy?.FirstName === 'Api' ||
                        comment?.CreatedBy?.FirstName === 'API' ||
                        comment?.CreatedBy?.FirstName === 'My' ||
                        comment?.CreatedBy?.FirstName === 'Salesforce'
                          ? 'Comcast Business'
                          : (comment?.CreatedBy?.FirstName | titlecase)
                      }}
                    }
                    @if (
                      !comment?.Contact__r?.Name && comment?.User__r?.FirstName
                    ) {
                      {{
                        comment.User__r?.FirstName === 'System'
                          ? 'Comcast Business'
                          : (comment.User__r?.FirstName | titlecase)
                      }}
                    }
                  </div>
                  <div
                    class="font-lato shrink-0 text-[11px] whitespace-nowrap text-gray-400 lg:text-xs"
                  >
                    {{ comment.CreatedDate | date: 'h:mma, MMM d, y' }}
                  </div>
                </div>
                <div
                  class="font-lato mt-0.5 rounded-lg p-3 text-sm text-black lg:p-4"
                  [class.bg-blue-75]="isMyComment(comment)"
                  [class.bg-indigo-50]="isSystemComment(comment)"
                  [class.bg-gray-100]="
                    !isMyComment(comment) && !isSystemComment(comment)
                  "
                  [innerHtml]="comment?.Body__c"
                  role="article"
                  aria-label="Comment"
                ></div>
              </div>
            </div>
          </div>
        }
      </div>
    </section>
  }

  <div class="hidden lg:block"></div>
</section>
